import { useState, useEffect } from 'react'
import s from './EmailTemplateSection.scss'
import { CreateEmailTemplateModal } from './CreateEmailTemplateModal'
import { List } from './List'
import { CONTACT_EMAIL, FOLLOW_UP_EMAIL, SEND_PROPOSAL_EMAIL } from './constants'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import update from 'immutability-helper'
import swal from 'sweetalert'
import { AddButton } from 'components/common/AddButton'
import { useAppBasedApi } from 'hooks'

const EmailTemplateSection = ({ title, type, description, fetchUrl, hideCreateBtn, isBulk }) => {
  const reduxDispatch = useDispatch()
  const [isCreateEmailTemplateModalVisible, setIsCreateEmailTemplateModalVisible] = useState(false)
  const [templates, setTemplates] = useState([])
  const [isEditedTemplate, setIsEditedTemplate] = useState(null)
  const { getRequest, postRequest, putRequest, deleteRequest } = useAppBasedApi(type)

  useEffect(() => {
    ;(async () => {
      try {
        const response = await getRequest(fetchUrl)
        setTemplates(response)
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'An error occurred while fetching your email templates. Please contact support',
            level: 'error'
          }
        })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl])

  const deleteTemplate = async id => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this email template?',
      text: 'This action can’t be undone',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        await deleteRequest(`/v1/email/templates/${id}`)
        const array = [...templates]
        const index = templates.findIndex(u => u.id === id)
        if (~index) {
          array.splice(index, 1)
          setTemplates(array)
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'Template deleted successfuly',
              level: 'success'
            }
          })
        }
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem deleting template',
            level: 'error'
          }
        })
      }
    }
  }

  const openEditModal = async template => {
    const { content, subject } = await getRequest(`/v1/email/templates/${template.id}`)
    setIsEditedTemplate({ ...template, content, title: template.name, subject })
    setIsCreateEmailTemplateModalVisible(true)
  }

  const saveTemplate = async (template, title, subject, destroyEditor) => {
    const payload = {
      content: template,
      title,
      subject,
      ...(type !== 'leads'
        ? {
            category:
              type === 'followup'
                ? FOLLOW_UP_EMAIL
                : type === 'proposal'
                  ? SEND_PROPOSAL_EMAIL
                  : CONTACT_EMAIL
          }
        : {})
    }
    let response
    try {
      if (isEditedTemplate?.id) {
        response = await putRequest(`/v1/email/templates/${isEditedTemplate.id}`, payload)
        const index = templates.findIndex(t => t.id === response.id)
        const newState = update(templates, {
          [index]: {
            $set: { ...response, name: response.title }
          }
        })
        setTemplates(newState)
      } else {
        if (type === 'leads' && isBulk) {
          response = await postRequest('/v1/email/templates/bulk', payload)
        } else {
          response = await postRequest('/v1/email/templates', payload)
        }
        setTemplates([...templates, { ...response, name: response.title }])
      }
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template saved successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem saving template',
          level: 'error'
        }
      })
    }
    destroyEditor && destroyEditor()
    setIsCreateEmailTemplateModalVisible(false)
    setIsEditedTemplate(null)
  }

  return (
    <div className={s.wrapper}>
      <h5 className={s.type}>{title}</h5>
      <p className={s.description}>{description}</p>
      {!hideCreateBtn && (
        <AddButton
          onClick={() => setIsCreateEmailTemplateModalVisible(true)}
          className={s.greenBtn}
        >
          Add {title}
        </AddButton>
      )}
      {templates.length > 0 && (
        <List templates={templates} deleteTemplate={deleteTemplate} openEditModal={openEditModal} />
      )}
      {isCreateEmailTemplateModalVisible && (
        <CreateEmailTemplateModal
          saveTemplate={saveTemplate}
          isCreateEmailTemplateModalVisible={isCreateEmailTemplateModalVisible}
          setIsCreateEmailTemplateModalVisible={setIsCreateEmailTemplateModalVisible}
          setIsEditedTemplate={setIsEditedTemplate}
          template={isEditedTemplate}
          hasSubjectLine={type !== 'proposal'}
        />
      )}
    </div>
  )
}

export default EmailTemplateSection
